'use client';

import type { PublicCourse } from '@arcane-ai/core/publishing/public';
import {
  ButtonLink,
  Icon,
  type IconProps,
  Typography
} from '@arcane-ai/ui/components/atoms';
import { cn } from '@arcane-ai/ui/utils/cn';
import { localized } from '@arcane-ai/ui/utils/localized';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';

export interface PublicCoursePageProps {
  lng: string;
  course: PublicCourse;
  /** The teacher's public page, when they have one. */
  teacherHref?: string;
  /** Signed in: the course in the app; otherwise sign-up (rule 13). */
  signedIn: boolean;
}

/** A course's public outline (docs/specs/public-catalog.md, rule 10). */
export function PublicCoursePage({
  lng,
  course,
  teacherHref,
  signedIn
}: PublicCoursePageProps) {
  const { t } = useTranslation('public');
  const description = localized(course.description, lng);

  return (
    <article className={'mx-auto max-w-3xl space-y-8 py-6 md:py-10'}>
      <Link
        href={`/${lng}/courses`}
        className={'text-sm text-primary underline-offset-4 hover:underline'}
      >
        {t('course.allCourses')}
      </Link>
      <header className={'flex items-start gap-4'}>
        <span
          className={cn(
            'grid h-14 w-14 shrink-0 place-items-center rounded-2xl text-white',
            course.color ?? 'bg-primary'
          )}
        >
          <Icon
            name={(course.icon ?? 'BookOpen') as IconProps['name']}
            className={'h-7 w-7'}
          />
        </span>
        <div className={'min-w-0 space-y-2'}>
          <Typography
            variant={'h1'}
            dir={'auto'}
            className={'text-3xl font-bold md:text-4xl'}
          >
            {localized(course.title, lng)}
          </Typography>
          <p className={'text-sm text-muted-foreground'}>
            {course.teacher ? (
              teacherHref ? (
                <Link
                  href={teacherHref}
                  className={'text-primary underline-offset-4 hover:underline'}
                >
                  {t('course.by', { name: course.teacher.name })}
                </Link>
              ) : (
                t('course.by', { name: course.teacher.name })
              )
            ) : (
              t('course.byUs')
            )}
          </p>
        </div>
      </header>
      {description && (
        <p
          dir={'auto'}
          className={'whitespace-pre-line text-lg leading-relaxed'}
        >
          {description}
        </p>
      )}

      <section
        className={'rounded-2xl border border-border bg-card p-5 space-y-3'}
      >
        <p>{t('course.ctaHint')}</p>
        <ButtonLink
          href={signedIn ? `/${lng}/app/chat/${course.id}` : `/${lng}/signup`}
          variant={'primary'}
        >
          {signedIn ? t('course.openCourse') : t('course.startFree')}
        </ButtonLink>
      </section>

      {course.units.length > 0 && (
        <section className={'space-y-4'}>
          <Typography variant={'h2'} className={'text-2xl font-bold'}>
            {t('course.units')}
          </Typography>
          <ol className={'space-y-3'}>
            {course.units.map((unit) => {
              const unitDescription = localized(unit.description, lng);
              return (
                <li
                  key={unit.no}
                  id={`unit-${unit.no}`}
                  className={'rounded-xl border border-border p-4'}
                >
                  <p className={'text-xs font-medium text-muted-foreground'}>
                    {t('course.unit', { no: unit.no })}
                  </p>
                  <h3 dir={'auto'} className={'font-semibold'}>
                    {localized(unit.name, lng)}
                  </h3>
                  {unitDescription && (
                    <p
                      dir={'auto'}
                      className={'mt-1 text-sm text-muted-foreground'}
                    >
                      {unitDescription}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
        </section>
      )}
    </article>
  );
}
